// i18n.js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const shared = {
  heroTitle: "How can I help with groundwater today?",
  tryAsking: "Try asking",
  suggestions: "Suggestions",
  inputPlaceholder: "Ask about groundwater in any region...",
  inputDisclaimer: "Jalmitra can make mistakes. Verify important groundwater figures against official CGWB sources.",
  thinking: "Thinking...",
  backToChat: "Back to chat",
  nav: {
    subtitle: "Groundwater Data & Advisory Platform",
    map: "Map",
    visualize: "Visualize",
    voice: "Voice",
  },
  sidebar: {
    newChat: "New Chat",
    yourRole: "Your Role",
    explore: "Explore",
    clear: "Clear",
    chattingAs: "Chatting as",
  },
  actions: {
    compare: "Compare States",
    forecast: "Forecast",
    advisory: "Farmer Advisory",
    simulator: "What-If Simulator",
    fieldData: "Submit Field Data",
    reports: "Reports",
  },
  pages: {
    map: { title: "India Groundwater Map", subtitle: "Live groundwater levels across every state and union territory" },
    compare: { title: "Comparative Dashboard", subtitle: "Compare groundwater data across states, years, and metrics" },
    forecast: { title: "Groundwater Forecasting", subtitle: "Predictive analysis of groundwater extraction and availability trends" },
    advisory: { title: "Farmer Advisory", subtitle: "Sowing & irrigation recommendations for your crop and location" },
    simulator: { title: "What-If Simulator", subtitle: "Adjust agricultural draft and see the projected impact on groundwater extraction" },
    fieldData: { title: "Community Field Data", subtitle: "Submit a real well-depth reading to help widen data coverage beyond official Kerala-only district detail." },
    reports: { title: "Groundwater Report", subtitle: "Generate a PDF report with data table + AI-written narrative summary" },
    visualization: { title: "Data Visualization", subtitle: "Build custom charts from groundwater datasets" },
  },
  about: {
    tabs: { about: "About", terms: "Terms & Conditions", support: "Help & Support" },
    tagline: "Groundwater intelligence for India, designed and built by VioniX",
    footerLine1: "© 2026 Jalmitra",
    footerLine2: "A VioniX product",
    whatIsTitle: "What is Jalmitra?",
    whatIsBody: "Jalmitra is a groundwater intelligence platform built on India's Central Ground Water Board (CGWB) assessment data. It turns dense annual resource assessments into natural-language answers, forecasts, and farmer advisories — so that farmers, policymakers, researchers, and the general public can understand groundwater conditions without needing to read a technical report.",
    whatItDoesTitle: "What it does",
    features: [
      "Conversational Q&A over official groundwater datasets, in six Indian languages",
      "State and district comparisons across years and metrics",
      "Predictive forecasting of extraction and availability trends",
      "Crop-specific sowing and irrigation advisory for farmers",
      "Community-submitted field readings and PDF reporting",
    ],
    devTitle: "Developed by VioniX",
    devBody: "Jalmitra is designed and developed by VioniX, a small product studio focused on building practical, data-driven tools for public good. VioniX handles the end-to-end product — design, engineering, and the AI systems that power Jalmitra's conversational and analytical features.",
    lastUpdated: "Last updated 2026",
    clauses: [
      { title: "1. Acceptance of terms", body: "By accessing or using Jalmitra, you agree to these Terms & Conditions. If you do not agree, please discontinue use of the platform." },
      { title: "2. Nature of the service", body: "Jalmitra provides informational summaries, forecasts, and recommendations derived from CGWB groundwater assessment data and related public datasets. It is a decision-support tool, not a substitute for official surveys, licensed hydrogeological advice, or regulatory approval." },
      { title: "3. Data accuracy", body: "While we take reasonable care to keep underlying datasets current, groundwater assessments are published periodically and may not reflect real-time conditions. Figures, forecasts, and AI-generated narrative summaries can contain errors — always verify critical numbers against official CGWB sources before acting on them." },
      { title: "4. No warranty", body: "Jalmitra is provided \"as is\" without warranties of any kind, express or implied, including fitness for a particular purpose. VioniX does not guarantee uninterrupted availability of the service." },
      { title: "5. Limitation of liability", body: "VioniX shall not be held liable for any loss or damage arising from decisions made based on information provided by Jalmitra, including agricultural, financial, or policy decisions." },
      { title: "6. Community-submitted data", body: "Field observations submitted by users are clearly labeled as community-reported and unverified. They are never silently merged with official CGWB records." },
      { title: "7. Acceptable use", body: "You agree not to misuse the platform, including attempting to disrupt its infrastructure, scrape data at scale without permission, or submit false field observations." },
      { title: "8. Changes to these terms", body: "These terms may be updated from time to time as the platform evolves. Continued use of Jalmitra after changes constitutes acceptance of the revised terms." },
    ],
    contactTitle: "Contact VioniX Support",
    contactBody: "For bug reports, data corrections, partnership queries, or anything else — reach out and we'll get back to you.",
    faqTitle: "Frequently asked",
    faq: [
      { q: "Where does the underlying data come from?", a: "Groundwater figures come from the Assessment of Dynamic Ground Water Resources of India, published annually by CGWB and State/UT Ground Water Departments." },
      { q: "Can I trust the AI-generated answers?", a: "Jalmitra cites its sources and is built to stay grounded in the underlying dataset, but like any AI system it can make mistakes — always cross-check figures that matter for a real decision." },
      { q: "How do I change the app language?", a: "Use the language switcher in the top navigation bar on any page — your choice is remembered across the whole app." },
      { q: "How is my submitted field data used?", a: "Community field observations are stored separately from official records and are always labeled as unverified, community-reported data." },
    ],
  },
};

i18n.use(initReactI18next).init({
  resources: {
    en: {
      translation: {
        ...shared,
        greeting: "Hello! I'm Jalmitra, your groundwater intelligence assistant. I can help you access groundwater resource data, historical assessments, and provide insights about India's dynamic groundwater resources. How can I assist you today?",
        chatCleared: "Chat cleared. How can I help you with groundwater data today?",
        quickQueries: {
          q1: "Show groundwater levels in Karnataka",
          q2: "Critical blocks in Maharashtra",
          q3: "Historical data for Tamil Nadu",
          q4: "Recharge estimation methodology",
          q5: "Over-exploited areas in Punjab",
          q6: "Groundwater quality assessment",
          q7: "Groundwater quality in Kerala",
          q8: "Current assessment in Rajasthan"
        },
        roles: {
          farmer: {
            title: "Farmer",
            description: "Practical recommendations for agricultural water management",
            focus: "Get actionable insights for crop planning, irrigation guidance, and well management"
          },
          policymaker: {
            title: "Policymaker",
            description: "Policy insights and governance recommendations",
            focus: "Access policy-relevant data, regulatory insights, and governance recommendations"
          },
          researcher: {
            title: "Researcher",
            description: "Detailed data and technical analysis",
            focus: "Comprehensive datasets, methodologies, and detailed technical analysis"
          },
          general: {
            title: "General User",
            description: "General information and basic insights",
            focus: "Easy-to-understand information about groundwater resources"
          }
        }
      }
    },
    hi: {
      translation: {
        heroTitle: "आज मैं भूजल के बारे में आपकी कैसे मदद कर सकता हूँ?",
        tryAsking: "यह पूछ कर देखें",
        suggestions: "सुझाव",
        inputPlaceholder: "किसी भी क्षेत्र के भूजल के बारे में पूछें...",
        inputDisclaimer: "जलमित्र गलतियाँ कर सकता है। महत्वपूर्ण भूजल आँकड़ों की पुष्टि आधिकारिक CGWB स्रोतों से करें।",
        thinking: "सोच रहा हूँ...",
        backToChat: "चैट पर वापस जाएँ",
        nav: {
          subtitle: "भूजल डेटा और सलाहकार मंच",
          map: "मानचित्र",
          visualize: "विज़ुअलाइज़ करें",
          voice: "आवाज़",
        },
        sidebar: {
          newChat: "नई चैट",
          yourRole: "आपकी भूमिका",
          explore: "अन्वेषण करें",
          clear: "साफ़ करें",
          chattingAs: "इस रूप में बात कर रहे हैं",
        },
        actions: {
          compare: "राज्यों की तुलना करें",
          forecast: "पूर्वानुमान",
          advisory: "किसान सलाह",
          simulator: "व्हाट-इफ़ सिम्युलेटर",
          fieldData: "फ़ील्ड डेटा जमा करें",
          reports: "रिपोर्ट",
        },
        pages: {
          map: { title: "भारत भूजल मानचित्र", subtitle: "हर राज्य और केंद्र शासित प्रदेश का लाइव भूजल स्तर" },
          compare: { title: "तुलनात्मक डैशबोर्ड", subtitle: "राज्यों, वर्षों और मापदंडों में भूजल डेटा की तुलना करें" },
          forecast: { title: "भूजल पूर्वानुमान", subtitle: "भूजल निष्कर्षण और उपलब्धता रुझानों का पूर्वानुमान विश्लेषण" },
          advisory: { title: "किसान सलाह", subtitle: "आपकी फ़सल और स्थान के लिए बुवाई और सिंचाई संबंधी सुझाव" },
          simulator: { title: "व्हाट-इफ़ सिम्युलेटर", subtitle: "कृषि उपयोग में बदलाव करें और भूजल निष्कर्षण पर अनुमानित प्रभाव देखें" },
          fieldData: { title: "सामुदायिक फ़ील्ड डेटा", subtitle: "आधिकारिक केरल-केंद्रित ज़िला विवरण से आगे डेटा को व्यापक बनाने में मदद के लिए एक वास्तविक कुएँ की गहराई दर्ज करें।" },
          reports: { title: "भूजल रिपोर्ट", subtitle: "डेटा तालिका और AI-लिखित सारांश के साथ एक PDF रिपोर्ट बनाएँ" },
          visualization: { title: "डेटा विज़ुअलाइज़ेशन", subtitle: "भूजल डेटासेट से कस्टम चार्ट बनाएँ" },
        },
        about: {
          tabs: { about: "परिचय", terms: "नियम एवं शर्तें", support: "सहायता एवं समर्थन" },
          tagline: "भारत के लिए भूजल बुद्धिमत्ता, VioniX द्वारा डिज़ाइन और विकसित",
          footerLine1: "© 2026 जलमित्र",
          footerLine2: "एक VioniX उत्पाद",
          whatIsTitle: "जलमित्र क्या है?",
          whatIsBody: "जलमित्र भारत के केंद्रीय भूजल बोर्ड (CGWB) के आकलन डेटा पर आधारित एक भूजल बुद्धिमत्ता मंच है। यह घने वार्षिक संसाधन आकलनों को सरल भाषा में उत्तरों, पूर्वानुमानों, किसान सलाह और पूर्व-चेतावनी अलर्ट में बदल देता है — ताकि किसान, नीति निर्माता, शोधकर्ता और आम जनता बिना किसी तकनीकी रिपोर्ट को पढ़े भूजल की स्थिति समझ सकें।",
          whatItDoesTitle: "यह क्या करता है",
          features: [
            "छह भारतीय भाषाओं में आधिकारिक भूजल डेटासेट पर संवादात्मक प्रश्नोत्तर",
            "वर्षों और मापदंडों में राज्य और ज़िला तुलना",
            "निष्कर्षण और उपलब्धता रुझानों का पूर्वानुमान विश्लेषण",
            "किसानों के लिए फ़सल-विशिष्ट बुवाई और सिंचाई सलाह",
            "सामुदायिक फ़ील्ड रीडिंग और PDF रिपोर्टिंग",
          ],
          devTitle: "VioniX द्वारा विकसित",
          devBody: "जलमित्र को VioniX द्वारा डिज़ाइन और विकसित किया गया है, जो सार्वजनिक भलाई के लिए व्यावहारिक, डेटा-संचालित उपकरण बनाने पर केंद्रित एक छोटा प्रोडक्ट स्टूडियो है। VioniX पूरे उत्पाद को संभालता है — डिज़ाइन, इंजीनियरिंग, और जलमित्र की संवादात्मक व विश्लेषणात्मक क्षमताओं को शक्ति देने वाले AI सिस्टम।",
          lastUpdated: "अंतिम अद्यतन 2026",
          clauses: [
            { title: "1. शर्तों की स्वीकृति", body: "जलमित्र का उपयोग करके, आप इन नियमों और शर्तों से सहमत होते हैं। यदि आप सहमत नहीं हैं, तो कृपया इस मंच का उपयोग बंद करें।" },
            { title: "2. सेवा की प्रकृति", body: "जलमित्र CGWB भूजल आकलन डेटा और संबंधित सार्वजनिक डेटासेट से प्राप्त सूचनात्मक सारांश, पूर्वानुमान और सिफारिशें प्रदान करता है। यह एक निर्णय-सहायता उपकरण है, आधिकारिक सर्वेक्षणों, लाइसेंस प्राप्त जलभूवैज्ञानिक सलाह, या नियामक अनुमोदन का विकल्प नहीं है।" },
            { title: "3. डेटा सटीकता", body: "हालांकि हम अंतर्निहित डेटासेट को अद्यतन रखने का उचित ध्यान रखते हैं, भूजल आकलन समय-समय पर प्रकाशित होते हैं और वास्तविक समय की स्थिति को प्रतिबिंबित नहीं कर सकते। आंकड़े, पूर्वानुमान और AI-जनित सारांश में त्रुटियाँ हो सकती हैं — किसी भी महत्वपूर्ण निर्णय से पहले आधिकारिक CGWB स्रोतों से आंकड़ों की पुष्टि अवश्य करें।" },
            { title: "4. कोई वारंटी नहीं", body: "जलमित्र बिना किसी प्रकार की स्पष्ट या निहित वारंटी के \"जैसा है\" प्रदान किया जाता है। VioniX सेवा की निरंतर उपलब्धता की गारंटी नहीं देता।" },
            { title: "5. दायित्व की सीमा", body: "जलमित्र द्वारा प्रदान की गई जानकारी के आधार पर लिए गए कृषि, वित्तीय या नीतिगत निर्णयों से उत्पन्न किसी भी हानि या क्षति के लिए VioniX उत्तरदायी नहीं होगा।" },
            { title: "6. सामुदायिक डेटा", body: "उपयोगकर्ताओं द्वारा सबमिट किए गए फ़ील्ड अवलोकन स्पष्ट रूप से सामुदायिक-रिपोर्टेड और असत्यापित के रूप में लेबल किए जाते हैं। इन्हें कभी भी चुपचाप आधिकारिक CGWB रिकॉर्ड के साथ नहीं मिलाया जाता।" },
            { title: "7. उचित उपयोग", body: "आप मंच का दुरुपयोग न करने के लिए सहमत हैं, जिसमें इसके इंफ्रास्ट्रक्चर को बाधित करने, बिना अनुमति बड़े पैमाने पर डेटा स्क्रैप करने, या झूठे फ़ील्ड अवलोकन सबमिट करने का प्रयास शामिल है।" },
            { title: "8. इन शर्तों में परिवर्तन", body: "मंच के विकसित होने के साथ ये शर्तें समय-समय पर अपडेट की जा सकती हैं। परिवर्तनों के बाद जलमित्र का निरंतर उपयोग संशोधित शर्तों की स्वीकृति माना जाएगा।" },
          ],
          contactTitle: "VioniX सहायता से संपर्क करें",
          contactBody: "बग रिपोर्ट, डेटा सुधार, साझेदारी प्रश्न, या किसी अन्य विषय के लिए — संपर्क करें और हम आपसे जुड़ेंगे।",
          faqTitle: "अक्सर पूछे जाने वाले प्रश्न",
          faq: [
            { q: "अंतर्निहित डेटा कहाँ से आता है?", a: "भूजल के आंकड़े भारत के डायनामिक ग्राउंड वाटर रिसोर्सेज़ के आकलन से आते हैं, जो CGWB और राज्य/केंद्र शासित प्रदेश भूजल विभागों द्वारा प्रतिवर्ष प्रकाशित होता है।" },
            { q: "क्या मैं AI-जनित उत्तरों पर भरोसा कर सकता हूँ?", a: "जलमित्र अपने स्रोतों का हवाला देता है और अंतर्निहित डेटासेट पर आधारित रहने के लिए बनाया गया है, लेकिन किसी भी AI सिस्टम की तरह इसमें गलतियाँ हो सकती हैं — किसी वास्तविक निर्णय के लिए महत्वपूर्ण आंकड़ों की हमेशा दोबारा जाँच करें।" },
            { q: "मैं ऐप की भाषा कैसे बदलूँ?", a: "किसी भी पेज के शीर्ष नेविगेशन बार में भाषा स्विचर का उपयोग करें — आपकी पसंद पूरे ऐप में याद रखी जाती है।" },
            { q: "मेरा सबमिट किया गया फ़ील्ड डेटा कैसे उपयोग होता है?", a: "सामुदायिक फ़ील्ड अवलोकन आधिकारिक रिकॉर्ड से अलग संग्रहीत किए जाते हैं और हमेशा असत्यापित, सामुदायिक-रिपोर्टेड डेटा के रूप में लेबल किए जाते हैं।" },
          ],
        },
        greeting: "नमस्ते! मैं इन्ग्रेस एआई सहायक हूँ। मैं आपको भूजल संसाधन डेटा, ऐतिहासिक आकलन और भारत के गतिशील भूजल संसाधनों के बारे में जानकारी प्रदान कर सकता हूँ। मैं आपकी कैसे मदद कर सकता हूँ?",
        chatCleared: "चैट साफ़ हो गई। आज मैं आपकी भूजल डेटा में कैसे मदद कर सकता हूँ?",
        quickQueries: {
          q1: "कर्नाटक में भूजल स्तर दिखाएँ",
          q2: "महाराष्ट्र में महत्वपूर्ण ब्लॉक",
          q3: "तमिलनाडु का ऐतिहासिक डेटा",
          q4: "रिचार्ज आकलन की पद्धति",
          q5: "पंजाब में अति-शोषित क्षेत्र",
          q6: "भूजल गुणवत्ता का आकलन",
          q7: "केरल में भूजल गुणवत्ता",
          q8: "राजस्थान में वर्तमान आकलन"
        },
        roles: {
          farmer: {
            title: "किसान",
            description: "कृषि जल प्रबंधन के लिए व्यावहारिक सुझाव",
            focus: "फसल योजना, सिंचाई मार्गदर्शन और कुएँ प्रबंधन के लिए उपयोगी जानकारी प्राप्त करें"
          },
          policymaker: {
            title: "नीति निर्माता",
            description: "नीति अंतर्दृष्टि और शासन सुझाव",
            focus: "नीति-संबंधित डेटा, नियामक जानकारी और शासन सिफारिशें प्राप्त करें"
          },
          researcher: {
            title: "शोधकर्ता",
            description: "विस्तृत डेटा और तकनीकी विश्लेषण",
            focus: "व्यापक डेटासेट, पद्धतियाँ और विस्तृत तकनीकी विश्लेषण"
          },
          general: {
            title: "सामान्य उपयोगकर्ता",
            description: "सामान्य जानकारी और बुनियादी अंतर्दृष्टि",
            focus: "भूजल संसाधनों के बारे में आसान जानकारी प्राप्त करें"
          }
        }
      }
    },
    ml: {
      translation: {
        heroTitle: "ഇന്ന് ഭൂഗർഭജലത്തെക്കുറിച്ച് ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കും?",
        tryAsking: "ഇങ്ങനെ ചോദിച്ചു നോക്കൂ",
        suggestions: "നിർദ്ദേശങ്ങൾ",
        inputPlaceholder: "ഏത് പ്രദേശത്തെയും ഭൂഗർഭജലത്തെക്കുറിച്ച് ചോദിക്കൂ...",
        inputDisclaimer: "ജൽമിത്രയ്ക്ക് തെറ്റുകൾ സംഭവിക്കാം. പ്രധാനപ്പെട്ട ഭൂഗർഭജല കണക്കുകൾ ഔദ്യോഗിക CGWB സ്രോതസ്സുകൾ ഉപയോഗിച്ച് പരിശോധിക്കുക.",
        thinking: "ചിന്തിക്കുന്നു...",
        backToChat: "ചാറ്റിലേക്ക് മടങ്ങുക",
        nav: {
          subtitle: "ഭൂഗർഭജല ഡാറ്റയും ഉപദേശക പ്ലാറ്റ്‌ഫോമും",
          map: "മാപ്പ്",
          visualize: "ദൃശ്യവൽക്കരിക്കുക",
          voice: "ശബ്ദം",
        },
        sidebar: {
          newChat: "പുതിയ ചാറ്റ്",
          yourRole: "നിങ്ങളുടെ റോൾ",
          explore: "പര്യവേക്ഷണം ചെയ്യുക",
          clear: "മായ്‌ക്കുക",
          chattingAs: "ഈ റോളിൽ ചാറ്റ് ചെയ്യുന്നു",
        },
        actions: {
          compare: "സംസ്ഥാനങ്ങൾ താരതമ്യം ചെയ്യുക",
          forecast: "പ്രവചനം",
          advisory: "കർഷക ഉപദേശം",
          simulator: "വാട്ട്-ഇഫ് സിമുലേറ്റർ",
          fieldData: "ഫീൽഡ് ഡാറ്റ സമർപ്പിക്കുക",
          reports: "റിപ്പോർട്ടുകൾ",
        },
        pages: {
          map: { title: "ഇന്ത്യ ഭൂഗർഭജല മാപ്പ്", subtitle: "എല്ലാ സംസ്ഥാനങ്ങളിലെയും കേന്ദ്രഭരണ പ്രദേശങ്ങളിലെയും തത്സമയ ഭൂഗർഭജല നിലവാരം" },
          compare: { title: "താരതമ്യ ഡാഷ്‌ബോർഡ്", subtitle: "സംസ്ഥാനങ്ങൾ, വർഷങ്ങൾ, അളവുകൾ എന്നിവയിലുടനീളം ഭൂഗർഭജല ഡാറ്റ താരതമ്യം ചെയ്യുക" },
          forecast: { title: "ഭൂഗർഭജല പ്രവചനം", subtitle: "ഭൂഗർഭജല ഉപയോഗത്തിന്റെയും ലഭ്യതയുടെയും പ്രവണതകളുടെ പ്രവചന വിശകലനം" },
          advisory: { title: "കർഷക ഉപദേശം", subtitle: "നിങ്ങളുടെ വിളയ്ക്കും സ്ഥലത്തിനും അനുയോജ്യമായ വിതയ്ക്കൽ & ജലസേചന നിർദ്ദേശങ്ങൾ" },
          simulator: { title: "വാട്ട്-ഇഫ് സിമുലേറ്റർ", subtitle: "കാർഷിക ഉപയോഗം ക്രമീകരിച്ച് ഭൂഗർഭജല ഉപയോഗത്തിലെ പ്രതീക്ഷിത സ്വാധീനം കാണുക" },
          fieldData: { title: "കമ്മ്യൂണിറ്റി ഫീൽഡ് ഡാറ്റ", subtitle: "ഔദ്യോഗിക കേരള-മാത്ര ജില്ലാ വിശദാംശങ്ങൾക്കപ്പുറം ഡാറ്റ വ്യാപ്തി വിശാലമാക്കാൻ സഹായിക്കുന്ന ഒരു യഥാർത്ഥ കിണർ ആഴം റീഡിംഗ് സമർപ്പിക്കുക." },
          reports: { title: "ഭൂഗർഭജല റിപ്പോർട്ട്", subtitle: "ഡാറ്റ പട്ടികയും AI എഴുതിയ സംഗ്രഹവും ഉള്ള ഒരു PDF റിപ്പോർട്ട് സൃഷ്ടിക്കുക" },
          visualization: { title: "ഡാറ്റ ദൃശ്യവൽക്കരണം", subtitle: "ഭൂഗർഭജല ഡാറ്റാസെറ്റുകളിൽ നിന്ന് ഇഷ്ടാനുസൃത ചാർട്ടുകൾ നിർമ്മിക്കുക" },
        },
        about: {
          tabs: { about: "വിവരണം", terms: "നിബന്ധനകളും വ്യവസ്ഥകളും", support: "സഹായവും പിന്തുണയും" },
          tagline: "ഇന്ത്യക്കായുള്ള ഭൂഗർഭജല ബുദ്ധി, VioniX രൂപകൽപ്പന ചെയ്ത് നിർമ്മിച്ചത്",
          footerLine1: "© 2026 ജൽമിത്ര",
          footerLine2: "ഒരു VioniX ഉൽപ്പന്നം",
          whatIsTitle: "എന്താണ് ജൽമിത്ര?",
          whatIsBody: "ഇന്ത്യയുടെ കേന്ദ്ര ഭൂഗർഭജല ബോർഡിന്റെ (CGWB) വിലയിരുത്തൽ ഡാറ്റയെ അടിസ്ഥാനമാക്കിയുള്ള ഒരു ഭൂഗർഭജല ബുദ്ധി പ്ലാറ്റ്‌ഫോമാണ് ജൽമിത്ര. ഇത് സാന്ദ്രമായ വാർഷിക വിഭവ വിലയിരുത്തലുകളെ സ്വാഭാവിക ഭാഷയിലുള്ള ഉത്തരങ്ങൾ, പ്രവചനങ്ങൾ, കർഷക ഉപദേശങ്ങൾ, മുൻകൂർ മുന്നറിയിപ്പ് അലേർട്ടുകൾ എന്നിവയാക്കി മാറ്റുന്നു — സാങ്കേതിക റിപ്പോർട്ട് വായിക്കാതെ തന്നെ കർഷകർക്കും നയരൂപകർക്കും ഗവേഷകർക്കും പൊതുജനത്തിനും ഭൂഗർഭജല അവസ്ഥ മനസ്സിലാക്കാൻ.",
          whatItDoesTitle: "ഇത് ചെയ്യുന്നത്",
          features: [
            "ആറ് ഇന്ത്യൻ ഭാഷകളിൽ ഔദ്യോഗിക ഭൂഗർഭജല ഡാറ്റാസെറ്റുകളിൽ സംഭാഷണാത്മക ചോദ്യോത്തരം",
            "വർഷങ്ങളിലും അളവുകളിലും സംസ്ഥാന, ജില്ലാ താരതമ്യങ്ങൾ",
            "ഉപയോഗത്തിന്റെയും ലഭ്യതയുടെയും പ്രവണതകളുടെ പ്രവചന വിശകലനം",
            "കർഷകർക്കായി വിള-നിർദ്ദിഷ്ട വിതയ്ക്കൽ, ജലസേചന ഉപദേശം",
            "കമ്മ്യൂണിറ്റി സമർപ്പിച്ച ഫീൽഡ് റീഡിംഗുകളും PDF റിപ്പോർട്ടിംഗും",
          ],
          devTitle: "VioniX വികസിപ്പിച്ചത്",
          devBody: "പൊതുനന്മയ്ക്കായി പ്രായോഗികവും ഡാറ്റ-അധിഷ്ഠിതവുമായ ഉപകരണങ്ങൾ നിർമ്മിക്കുന്നതിൽ ശ്രദ്ധ കേന്ദ്രീകരിക്കുന്ന ഒരു ചെറിയ പ്രോഡക്റ്റ് സ്റ്റുഡിയോ ആയ VioniX ആണ് ജൽമിത്ര രൂപകൽപ്പന ചെയ്ത് വികസിപ്പിച്ചത്. ഡിസൈൻ, എഞ്ചിനീയറിംഗ്, ജൽമിത്രയുടെ സംഭാഷണാത്മകവും വിശകലനപരവുമായ സവിശേഷതകൾക്ക് ശക്തി പകരുന്ന AI സിസ്റ്റങ്ങൾ എന്നിവ VioniX കൈകാര്യം ചെയ്യുന്നു.",
          lastUpdated: "അവസാനം അപ്ഡേറ്റ് ചെയ്തത് 2026",
          clauses: [
            { title: "1. നിബന്ധനകളുടെ അംഗീകാരം", body: "ജൽമിത്ര ഉപയോഗിക്കുന്നതിലൂടെ, നിങ്ങൾ ഈ നിബന്ധനകളും വ്യവസ്ഥകളും അംഗീകരിക്കുന്നു. നിങ്ങൾ യോജിക്കുന്നില്ലെങ്കിൽ, ദയവായി ഈ പ്ലാറ്റ്‌ഫോമിന്റെ ഉപയോഗം നിർത്തുക." },
            { title: "2. സേവനത്തിന്റെ സ്വഭാവം", body: "CGWB ഭൂഗർഭജല വിലയിരുത്തൽ ഡാറ്റയിൽ നിന്നും ബന്ധപ്പെട്ട പൊതു ഡാറ്റാസെറ്റുകളിൽ നിന്നും ഉരുത്തിരിഞ്ഞ വിവരണാത്മക സംഗ്രഹങ്ങൾ, പ്രവചനങ്ങൾ, ശുപാർശകൾ എന്നിവ ജൽമിത്ര നൽകുന്നു. ഇത് ഒരു തീരുമാന-പിന്തുണാ ഉപകരണമാണ്, ഔദ്യോഗിക സർവേകൾക്കോ ലൈസൻസുള്ള ജലഭൂവിജ്ഞാന ഉപദേശത്തിനോ നിയന്ത്രണ അംഗീകാരത്തിനോ പകരമല്ല." },
            { title: "3. ഡാറ്റ കൃത്യത", body: "അടിസ്ഥാന ഡാറ്റാസെറ്റുകൾ കാലികമായി സൂക്ഷിക്കാൻ ഞങ്ങൾ ന്യായമായ ശ്രദ്ധ ചെലുത്തുന്നുണ്ടെങ്കിലും, ഭൂഗർഭജല വിലയിരുത്തലുകൾ ഇടയ്ക്കിടെ പ്രസിദ്ധീകരിക്കുന്നവയാണ്, തത്സമയ അവസ്ഥകളെ പ്രതിഫലിപ്പിച്ചെന്നു വരില്ല. കണക്കുകൾ, പ്രവചനങ്ങൾ, AI സൃഷ്ടിച്ച സംഗ്രഹങ്ങൾ എന്നിവയിൽ പിശകുകൾ ഉണ്ടാകാം — ഒരു യഥാർത്ഥ തീരുമാനത്തിന് പ്രധാനപ്പെട്ട കണക്കുകൾ എപ്പോഴും ഔദ്യോഗിക CGWB സ്രോതസ്സുകൾക്കെതിരെ പരിശോധിക്കുക." },
            { title: "4. വാറന്റി ഇല്ല", body: "ജൽമിത്ര ഏതെങ്കിലും തരത്തിലുള്ള വാറന്റിയില്ലാതെ \"നിലവിലുള്ള നിലയിൽ\" നൽകുന്നു. സേവനത്തിന്റെ തടസ്സമില്ലാത്ത ലഭ്യത VioniX ഉറപ്പുനൽകുന്നില്ല." },
            { title: "5. ബാധ്യതാ പരിധി", body: "ജൽമിത്ര നൽകുന്ന വിവരങ്ങളെ അടിസ്ഥാനമാക്കി എടുക്കുന്ന കാർഷിക, സാമ്പത്തിക, നയപരമായ തീരുമാനങ്ങളിൽ നിന്ന് ഉണ്ടാകുന്ന ഏതെങ്കിലും നഷ്ടത്തിനോ കേടുപാടിനോ VioniX ഉത്തരവാദിയായിരിക്കില്ല." },
            { title: "6. കമ്മ്യൂണിറ്റി ഡാറ്റ", body: "ഉപയോക്താക്കൾ സമർപ്പിക്കുന്ന ഫീൽഡ് നിരീക്ഷണങ്ങൾ കമ്മ്യൂണിറ്റി-റിപ്പോർട്ട് ചെയ്തതും പരിശോധിക്കാത്തതുമായി വ്യക്തമായി ലേബൽ ചെയ്യപ്പെടുന്നു. ഇവ ഒരിക്കലും ഔദ്യോഗിക CGWB രേഖകളുമായി നിശബ്ദമായി കൂട്ടിച്ചേർക്കില്ല." },
            { title: "7. സ്വീകാര്യമായ ഉപയോഗം", body: "പ്ലാറ്റ്‌ഫോമിന്റെ അടിസ്ഥാന സൗകര്യങ്ങൾ തടസ്സപ്പെടുത്താൻ ശ്രമിക്കുക, അനുമതിയില്ലാതെ വൻതോതിൽ ഡാറ്റ ശേഖരിക്കുക, അല്ലെങ്കിൽ വ്യാജ ഫീൽഡ് നിരീക്ഷണങ്ങൾ സമർപ്പിക്കുക എന്നിവ ഉൾപ്പെടെ പ്ലാറ്റ്‌ഫോം ദുരുപയോഗം ചെയ്യില്ലെന്ന് നിങ്ങൾ സമ്മതിക്കുന്നു." },
            { title: "8. ഈ നിബന്ധനകളിലെ മാറ്റങ്ങൾ", body: "പ്ലാറ്റ്‌ഫോം വികസിക്കുന്നതിനനുസരിച്ച് ഈ നിബന്ധനകൾ കാലാകാലങ്ങളിൽ അപ്ഡേറ്റ് ചെയ്യപ്പെടാം. മാറ്റങ്ങൾക്ക് ശേഷം ജൽമിത്ര തുടർന്നും ഉപയോഗിക്കുന്നത് പുതുക്കിയ നിബന്ധനകളുടെ അംഗീകാരമായി കണക്കാക്കും." },
          ],
          contactTitle: "VioniX പിന്തുണയുമായി ബന്ധപ്പെടുക",
          contactBody: "ബഗ് റിപ്പോർട്ടുകൾ, ഡാറ്റ തിരുത്തലുകൾ, പങ്കാളിത്ത അന്വേഷണങ്ങൾ, അല്ലെങ്കിൽ മറ്റെന്തെങ്കിലും — ബന്ധപ്പെടുക, ഞങ്ങൾ നിങ്ങളെ തിരികെ ബന്ധപ്പെടും.",
          faqTitle: "പതിവായി ചോദിക്കുന്നവ",
          faq: [
            { q: "അടിസ്ഥാന ഡാറ്റ എവിടെ നിന്നാണ് വരുന്നത്?", a: "CGWB ഉം സംസ്ഥാന/കേന്ദ്രഭരണ പ്രദേശ ഭൂഗർഭജല വകുപ്പുകളും വർഷം തോറും പ്രസിദ്ധീകരിക്കുന്ന ഇന്ത്യയുടെ ഡൈനാമിക് ഭൂഗർഭജല വിഭവങ്ങളുടെ വിലയിരുത്തലിൽ നിന്നാണ് ഭൂഗർഭജല കണക്കുകൾ ലഭിക്കുന്നത്." },
            { q: "AI സൃഷ്ടിച്ച ഉത്തരങ്ങളെ എനിക്ക് വിശ്വസിക്കാമോ?", a: "ജൽമിത്ര അതിന്റെ സ്രോതസ്സുകൾ ഉദ്ധരിക്കുകയും അടിസ്ഥാന ഡാറ്റാസെറ്റിൽ അധിഷ്ഠിതമായി തുടരാൻ നിർമ്മിക്കുകയും ചെയ്തിരിക്കുന്നു, പക്ഷേ ഏതൊരു AI സിസ്റ്റത്തെയും പോലെ ഇതിനും തെറ്റുകൾ സംഭവിക്കാം — ഒരു യഥാർത്ഥ തീരുമാനത്തിന് പ്രധാനപ്പെട്ട കണക്കുകൾ എപ്പോഴും വീണ്ടും പരിശോധിക്കുക." },
            { q: "ആപ്പിന്റെ ഭാഷ എങ്ങനെ മാറ്റാം?", a: "ഏത് പേജിലെയും മുകളിലെ നാവിഗേഷൻ ബാറിലുള്ള ഭാഷാ സ്വിച്ചർ ഉപയോഗിക്കുക — നിങ്ങളുടെ തിരഞ്ഞെടുപ്പ് ആപ്പ് മുഴുവനും ഓർമ്മിക്കപ്പെടും." },
            { q: "എന്റെ സമർപ്പിച്ച ഫീൽഡ് ഡാറ്റ എങ്ങനെയാണ് ഉപയോഗിക്കുന്നത്?", a: "കമ്മ്യൂണിറ്റി ഫീൽഡ് നിരീക്ഷണങ്ങൾ ഔദ്യോഗിക രേഖകളിൽ നിന്ന് വേറിട്ട് സൂക്ഷിക്കുകയും എപ്പോഴും പരിശോധിക്കാത്ത, കമ്മ്യൂണിറ്റി-റിപ്പോർട്ട് ചെയ്ത ഡാറ്റയായി ലേബൽ ചെയ്യുകയും ചെയ്യുന്നു." },
          ],
        },
        greeting: "നമസ്കാരം! ഞാൻ ജൽമിത്ര ആണ്, നിങ്ങളുടെ ഭൂഗർഭജല സഹായി. ഭൂഗർഭജല വിഭവങ്ങളുടെ ഡാറ്റ, ചരിത്ര വിലയിരുത്തലുകൾ, ഇന്ത്യയുടെ ഡൈനാമിക് ഭൂഗർഭജല വിഭവങ്ങൾ എന്നിവയെക്കുറിച്ചുള്ള വിവരങ്ങൾ ഞാൻ നിങ്ങൾക്ക് നൽകാം. ഞാൻ നിങ്ങളെ ഇന്ന് എങ്ങനെ സഹായിക്കും?",
        chatCleared: "ചാറ്റ് ക്ലിയർ ചെയ്തു. ഇന്ന് ഭൂഗർഭജല ഡാറ്റയിൽ ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കും?",
        quickQueries: {
          q1: "കർണാടകത്തിലെ ഭൂഗർഭജല നില കാണിക്കുക",
          q2: "മഹാരാഷ്ട്രയിലെ നിർണായക ബ്ലോക്കുകൾ",
          q3: "തമിഴ്നാടിന്റെ ചരിത്ര ഡാറ്റ",
          q4: "റീചാർജ് വിലയിരുത്തൽ രീതി",
          q5: "പഞ്ചാബിലെ അതിശോഷിത പ്രദേശങ്ങൾ",
          q6: "ഭൂഗർഭജല ഗുണനിലവാര വിലയിരുത്തൽ",
          q7: "കേരളത്തിലെ ഭൂഗർഭജല ഗുണനിലവാരം",
          q8: "രാജസ്ഥാനിലെ നിലവിലെ വിലയിരുത്തൽ"
        },
        roles: {
          farmer: {
            title: "കർഷകൻ",
            description: "കൃഷി ജല മാനേജ്മെന്റിനുള്ള പ്രായോഗിക നിർദ്ദേശങ്ങൾ",
            focus: "വിള പദ്ധതി, ജലസേചന മാർഗ്ഗനിർദ്ദേശങ്ങൾ, കിണർ മാനേജ്മെന്റ് സംബന്ധിച്ച ഉപകാരപ്രദമായ വിവരങ്ങൾ നേടുക"
          },
          policymaker: {
            title: "നയരൂപകൻ",
            description: "നയ അവലോകനങ്ങളും ഭരണ നിർദ്ദേശങ്ങളും",
            focus: "നയബന്ധിത ഡാറ്റ, നിയമനിർമ്മാണ വിവരങ്ങൾ, ഭരണ ശുപാർശകൾ നേടുക"
          },
          researcher: {
            title: "ഗവേഷകൻ",
            description: "വിശദമായ ഡാറ്റയും സാങ്കേതിക വിശകലനവും",
            focus: "വ്യാപകമായ ഡാറ്റാസെറ്റ്, രീതിശാസ്ത്രങ്ങൾ, സാങ്കേതിക വിശകലനം നേടുക"
          },
          general: {
            title: "സാധാരണ ഉപയോക്താവ്",
            description: "പൊതു വിവരങ്ങളും അടിസ്ഥാന അറിവുകളും",
            focus: "ഭൂഗർഭജല വിഭവങ്ങളെക്കുറിച്ച് എളുപ്പത്തിൽ അറിയുക"
          }
        }
      }
    },
    te: {
      translation: {
        heroTitle: "ఈరోజు భూగర్భజలం గురించి నేను మీకు ఎలా సహాయం చేయగలను?",
        tryAsking: "ఇలా అడగండి",
        suggestions: "సూచనలు",
        inputPlaceholder: "ఏదైనా ప్రాంతంలో భూగర్భజలం గురించి అడగండి...",
        inputDisclaimer: "జల్‌మిత్ర తప్పులు చేయవచ్చు. ముఖ్యమైన భూగర్భజల గణాంకాలను అధికారిక CGWB మూలాల ద్వారా ధృవీకరించండి.",
        thinking: "ఆలోచిస్తోంది...",
        backToChat: "చాట్‌కి తిరిగి వెళ్ళండి",
        nav: {
          subtitle: "భూగర్భజల డేటా మరియు సలహా వేదిక",
          map: "మ్యాప్",
          visualize: "విజువలైజ్ చేయండి",
          voice: "వాయిస్",
        },
        sidebar: {
          newChat: "కొత్త చాట్",
          yourRole: "మీ పాత్ర",
          explore: "అన్వేషించండి",
          clear: "క్లియర్ చేయండి",
          chattingAs: "ఈ పాత్రలో చాట్ చేస్తున్నారు",
        },
        actions: {
          compare: "రాష్ట్రాలను పోల్చండి",
          forecast: "అంచనా",
          advisory: "రైతు సలహా",
          simulator: "వాట్-ఇఫ్ సిమ్యులేటర్",
          fieldData: "ఫీల్డ్ డేటాను సమర్పించండి",
          reports: "నివేదికలు",
        },
        pages: {
          map: { title: "భారత భూగర్భజల మ్యాప్", subtitle: "ప్రతి రాష్ట్రం మరియు కేంద్రపాలిత ప్రాంతంలో ప్రత్యక్ష భూగర్భజల మట్టాలు" },
          compare: { title: "తులనాత్మక డాష్‌బోర్డ్", subtitle: "రాష్ట్రాలు, సంవత్సరాలు మరియు కొలమానాల వారీగా భూగర్భజల డేటాను పోల్చండి" },
          forecast: { title: "భూగర్భజల అంచనా", subtitle: "భూగర్భజల వినియోగం మరియు లభ్యత ధోరణుల అంచనా విశ్లేషణ" },
          advisory: { title: "రైతు సలహా", subtitle: "మీ పంట మరియు ప్రాంతానికి సంబంధించిన విత్తన & నీటిపారుదల సూచనలు" },
          simulator: { title: "వాట్-ఇఫ్ సిమ్యులేటర్", subtitle: "వ్యవసాయ వినియోగాన్ని సర్దుబాటు చేసి భూగర్భజల వెలికితీతపై అంచనా ప్రభావాన్ని చూడండి" },
          fieldData: { title: "సామాజిక ఫీల్డ్ డేటా", subtitle: "అధికారిక కేరళ-మాత్రమే జిల్లా వివరాలకు మించి డేటా కవరేజీని విస్తరించడంలో సహాయపడటానికి నిజమైన బావి లోతు రీడింగ్‌ను సమర్పించండి." },
          reports: { title: "భూగర్భజల నివేదిక", subtitle: "డేటా టేబుల్ మరియు AI-వ్రాసిన సారాంశంతో PDF నివేదికను రూపొందించండి" },
          visualization: { title: "డేటా విజువలైజేషన్", subtitle: "భూగర్భజల డేటాసెట్‌ల నుండి కస్టమ్ చార్ట్‌లను రూపొందించండి" },
        },
        about: {
          tabs: { about: "గురించి", terms: "నిబంధనలు మరియు షరతులు", support: "సహాయం మరియు మద్దతు" },
          tagline: "భారతదేశం కోసం భూగర్భజల ఇంటెలిజెన్స్, VioniX రూపొందించి అభివృద్ధి చేసింది",
          footerLine1: "© 2026 జల్‌మిత్ర",
          footerLine2: "ఒక VioniX ఉత్పత్తి",
          whatIsTitle: "జల్‌మిత్ర అంటే ఏమిటి?",
          whatIsBody: "భారతదేశ కేంద్ర భూగర్భ జల మండలి (CGWB) అంచనా డేటాపై నిర్మించిన భూగర్భజల ఇంటెలిజెన్స్ ప్లాట్‌ఫారమ్ జల్‌మిత్ర. ఇది సాంద్రమైన వార్షిక వనరుల అంచనాలను సహజ భాషలో సమాధానాలు, అంచనాలు, రైతు సలహాలు మరియు ముందస్తు-హెచ్చరిక అలర్ట్‌లుగా మారుస్తుంది — సాంకేతిక నివేదికను చదవాల్సిన అవసరం లేకుండా రైతులు, పాలసీ నిర్ణేతలు, పరిశోధకులు మరియు సాధారణ ప్రజలు భూగర్భజల పరిస్థితులను అర్థం చేసుకోగలరు.",
          whatItDoesTitle: "ఇది ఏమి చేస్తుంది",
          features: [
            "ఆరు భారతీయ భాషలలో అధికారిక భూగర్భజల డేటాసెట్‌లపై సంభాషణాత్మక ప్రశ్నోత్తరాలు",
            "సంవత్సరాలు మరియు కొలమానాల వారీగా రాష్ట్ర మరియు జిల్లా పోలికలు",
            "వెలికితీత మరియు లభ్యత ధోరణుల అంచనా విశ్లేషణ",
            "రైతుల కోసం పంట-నిర్దిష్ట విత్తనం మరియు నీటిపారుదల సలహా",
            "సామాజిక సమర్పిత ఫీల్డ్ రీడింగ్‌లు మరియు PDF నివేదిక",
          ],
          devTitle: "VioniX చే అభివృద్ధి చేయబడింది",
          devBody: "ప్రజా ప్రయోజనం కోసం ఆచరణాత్మక, డేటా-ఆధారిత సాధనాలను నిర్మించడంపై దృష్టి సారించిన చిన్న ప్రోడక్ట్ స్టూడియో అయిన VioniX చే జల్‌మిత్ర రూపొందించబడి అభివృద్ధి చేయబడింది. డిజైన్, ఇంజనీరింగ్, మరియు జల్‌మిత్ర యొక్క సంభాషణాత్మక మరియు విశ్లేషణాత్మక సామర్థ్యాలను శక్తివంతం చేసే AI వ్యవస్థలను VioniX నిర్వహిస్తుంది.",
          lastUpdated: "చివరిగా నవీకరించబడింది 2026",
          clauses: [
            { title: "1. నిబంధనల అంగీకారం", body: "జల్‌మిత్రను ఉపయోగించడం ద్వారా, మీరు ఈ నిబంధనలు మరియు షరతులకు అంగీకరిస్తున్నారు. మీరు అంగీకరించకపోతే, దయచేసి ఈ ప్లాట్‌ఫారమ్ వినియోగాన్ని నిలిపివేయండి." },
            { title: "2. సేవ యొక్క స్వభావం", body: "జల్‌మిత్ర CGWB భూగర్భజల అంచనా డేటా మరియు సంబంధిత పబ్లిక్ డేటాసెట్‌ల నుండి పొందిన సమాచార సారాంశాలు, అంచనాలు మరియు సిఫారసులను అందిస్తుంది. ఇది నిర్ణయ-మద్దతు సాధనం, అధికారిక సర్వేలు, లైసెన్స్ పొందిన జలభూవిజ్ఞాన సలహా, లేదా నియంత్రణ ఆమోదానికి ప్రత్యామ్నాయం కాదు." },
            { title: "3. డేటా ఖచ్చితత్వం", body: "అంతర్లీన డేటాసెట్‌లను తాజాగా ఉంచడానికి మేము తగిన శ్రద్ధ తీసుకుంటున్నప్పటికీ, భూగర్భజల అంచనాలు కాలానుగుణంగా ప్రచురించబడతాయి మరియు నిజ-సమయ పరిస్థితులను ప్రతిబింబించకపోవచ్చు. గణాంకాలు, అంచనాలు మరియు AI-రూపొందించిన సారాంశాలలో లోపాలు ఉండవచ్చు — ఏదైనా వాస్తవ నిర్ణయానికి ముఖ్యమైన గణాంకాలను ఎల్లప్పుడూ అధికారిక CGWB మూలాల ద్వారా ధృవీకరించండి." },
            { title: "4. వారంటీ లేదు", body: "జల్‌మిత్ర ఎలాంటి హామీలు లేకుండా \"ఉన్నది ఉన్నట్లుగా\" అందించబడుతుంది. సేవ యొక్క నిరంతర లభ్యతకు VioniX హామీ ఇవ్వదు." },
            { title: "5. బాధ్యత పరిమితి", body: "జల్‌మిత్ర అందించిన సమాచారం ఆధారంగా తీసుకున్న వ్యవసాయ, ఆర్థిక లేదా విధాన నిర్ణయాల నుండి ఉత్పన్నమయ్యే ఏదైనా నష్టం లేదా హానికి VioniX బాధ్యత వహించదు." },
            { title: "6. సామాజిక డేటా", body: "వినియోగదారులు సమర్పించిన ఫీల్డ్ పరిశీలనలు స్పష్టంగా సామాజిక-నివేదిత మరియు ధృవీకరించని విధంగా లేబుల్ చేయబడతాయి. ఇవి అధికారిక CGWB రికార్డులతో ఎప్పుడూ నిశ్శబ్దంగా విలీనం చేయబడవు." },
            { title: "7. ఆమోదయోగ్యమైన వినియోగం", body: "ప్లాట్‌ఫారమ్ మౌలిక సదుపాయాలకు అంతరాయం కలిగించడానికి ప్రయత్నించడం, అనుమతి లేకుండా భారీ స్థాయిలో డేటాను స్క్రాప్ చేయడం, లేదా తప్పుడు ఫీల్డ్ పరిశీలనలను సమర్పించడంతో సహా ప్లాట్‌ఫారమ్‌ను దుర్వినియోగం చేయకూడదని మీరు అంగీకరిస్తున్నారు." },
            { title: "8. ఈ నిబంధనలలో మార్పులు", body: "ప్లాట్‌ఫారమ్ అభివృద్ధి చెందుతున్న కొద్దీ ఈ నిబంధనలు ఎప్పటికప్పుడు నవీకరించబడవచ్చు. మార్పుల తర్వాత జల్‌మిత్రను కొనసాగించి ఉపయోగించడం సవరించిన నిబంధనల అంగీకారంగా పరిగణించబడుతుంది." },
          ],
          contactTitle: "VioniX మద్దతును సంప్రదించండి",
          contactBody: "బగ్ నివేదికలు, డేటా సవరణలు, భాగస్వామ్య ప్రశ్నలు, లేదా మరేదైనా కోసం — సంప్రదించండి, మేము మిమ్మల్ని తిరిగి సంప్రదిస్తాము.",
          faqTitle: "తరచుగా అడిగే ప్రశ్నలు",
          faq: [
            { q: "అంతర్లీన డేటా ఎక్కడ నుండి వస్తుంది?", a: "భూగర్భజల గణాంకాలు CGWB మరియు రాష్ట్ర/కేంద్రపాలిత ప్రాంతాల భూగర్భజల విభాగాలు ఏటా ప్రచురించే భారతదేశ డైనమిక్ గ్రౌండ్ వాటర్ రిసోర్సెస్ అంచనా నుండి వస్తాయి." },
            { q: "నేను AI-రూపొందించిన సమాధానాలను నమ్మవచ్చా?", a: "జల్‌మిత్ర తన మూలాలను ఉదహరిస్తుంది మరియు అంతర్లీన డేటాసెట్‌పై ఆధారపడి ఉండేలా నిర్మించబడింది, కానీ ఏ AI వ్యవస్థ లాగే ఇది తప్పులు చేయవచ్చు — ఒక వాస్తవ నిర్ణయానికి ముఖ్యమైన గణాంకాలను ఎల్లప్పుడూ క్రాస్-చెక్ చేయండి." },
            { q: "నేను యాప్ భాషను ఎలా మార్చాలి?", a: "ఏ పేజీలోనైనా టాప్ నావిగేషన్ బార్‌లోని లాంగ్వేజ్ స్విచర్‌ను ఉపయోగించండి — మీ ఎంపిక యాప్ మొత్తంలో గుర్తుంచుకోబడుతుంది." },
            { q: "నేను సమర్పించిన ఫీల్డ్ డేటా ఎలా ఉపయోగించబడుతుంది?", a: "సామాజిక ఫీల్డ్ పరిశీలనలు అధికారిక రికార్డుల నుండి వేరుగా నిల్వ చేయబడతాయి మరియు ఎల్లప్పుడూ ధృవీకరించని, సామాజిక-నివేదిత డేటాగా లేబుల్ చేయబడతాయి." },
          ],
        },
        greeting: "నమస్తే! నేను జల్‌మిత్రను, మీ భూగర్భజల సహాయకుడిని. నేను మీకు భూగర్భజల వనరుల డేటా, చారిత్రక అంచనాలు మరియు భారతదేశ భూగర్భజల వనరులపై సమాచారం అందించగలను. నేను మీకు ఈరోజు ఎలా సహాయం చేయగలను?",
        chatCleared: "చాట్ క్లియర్ చేయబడింది. ఈ రోజు నేను మీకు భూగర్భజల డేటాలో ఎలా సహాయం చేయగలను?",
        quickQueries: {
          q1: "కర్ణాటకలో భూగర్భజల స్థాయి చూపించు",
          q2: "మహారాష్ట్రలో ముఖ్యమైన బ్లాకులు",
          q3: "తమిళనాడు యొక్క చారిత్రక డేటా",
          q4: "రిచార్జ్ అంచనా పద్ధతి",
          q5: "పంజాబ్‌లో అతిగా వినియోగించిన ప్రాంతాలు",
          q6: "భూగర్భజల నాణ్యత అంచనా",
          q7: "కేరళలో భూగర్భజల నాణ్యత",
          q8: "రాజస్థాన్‌లో ప్రస్తుత అంచనా"
        },
        roles: {
          farmer: {
            title: "రైతు",
            description: "వ్యవసాయ నీటి నిర్వహణకు ఉపయోగకరమైన సూచనలు",
            focus: "పంట ప్రణాళిక, పంట నీరు సరఫరా మార్గదర్శకాలు మరియు బావి నిర్వహణకు సంబంధించిన సమాచారం పొందండి"
          },
          policymaker: {
            title: "పాలసీ నిర్ణేత",
            description: "పాలసీ అవగాహన మరియు పాలన సూచనలు",
            focus: "పాలసీకి సంబంధించిన డేటా, నియంత్రణ సమాచారం మరియు పాలన సిఫారసులు పొందండి"
          },
          researcher: {
            title: "పరిశోధకుడు",
            description: "వివరమైన డేటా మరియు సాంకేతిక విశ్లేషణ",
            focus: "విస్తృతమైన డేటాసెట్, విధానాలు మరియు సాంకేతిక విశ్లేషణ పొందండి"
          },
          general: {
            title: "సాధారణ వినియోగదారు",
            description: "సాధారణ సమాచారం మరియు ప్రాథమిక అవగాహన",
            focus: "భూగర్భజల వనరుల గురించి సులభమైన సమాచారం పొందండి"
          }
        }
      }
    },
    ta: {
      translation: {
        heroTitle: "இன்று நிலத்தடி நீர் குறித்து நான் உங்களுக்கு எப்படி உதவ முடியும்?",
        tryAsking: "இவ்வாறு கேட்டு பாருங்கள்",
        suggestions: "பரிந்துரைகள்",
        inputPlaceholder: "எந்தப் பகுதியிலும் நிலத்தடி நீர் பற்றி கேளுங்கள்...",
        inputDisclaimer: "ஜல்மித்ரா தவறுகள் செய்யக்கூடும். முக்கியமான நிலத்தடி நீர் புள்ளிவிவரங்களை அதிகாரப்பூர்வ CGWB மூலங்களுடன் சரிபார்க்கவும்.",
        thinking: "யோசிக்கிறது...",
        backToChat: "அரட்டைக்குத் திரும்பு",
        nav: {
          subtitle: "நிலத்தடி நீர் தரவு மற்றும் ஆலோசனை தளம்",
          map: "வரைபடம்",
          visualize: "காட்சிப்படுத்து",
          voice: "குரல்",
        },
        sidebar: {
          newChat: "புதிய அரட்டை",
          yourRole: "உங்கள் பங்கு",
          explore: "ஆராயுங்கள்",
          clear: "அழி",
          chattingAs: "இந்த பங்கில் அரட்டையடிக்கிறீர்கள்",
        },
        actions: {
          compare: "மாநிலங்களை ஒப்பிடு",
          forecast: "முன்னறிவிப்பு",
          advisory: "விவசாயி ஆலோசனை",
          simulator: "வாட்-இஃப் சிமுலேட்டர்",
          fieldData: "கள தரவு சமர்ப்பிக்க",
          reports: "அறிக்கைகள்",
        },
        pages: {
          map: { title: "இந்திய நிலத்தடி நீர் வரைபடம்", subtitle: "ஒவ்வொரு மாநிலம் மற்றும் யூனியன் பிரதேசத்தின் நேரடி நிலத்தடி நீர் மட்டங்கள்" },
          compare: { title: "ஒப்பீட்டு டாஷ்போர்டு", subtitle: "மாநிலங்கள், ஆண்டுகள் மற்றும் அளவீடுகள் வழியாக நிலத்தடி நீர் தரவை ஒப்பிடுங்கள்" },
          forecast: { title: "நிலத்தடி நீர் முன்னறிவிப்பு", subtitle: "நிலத்தடி நீர் பயன்பாடு மற்றும் கிடைக்கும் தன்மை போக்குகளின் முன்கணிப்பு பகுப்பாய்வு" },
          advisory: { title: "விவசாயி ஆலோசனை", subtitle: "உங்கள் பயிர் மற்றும் இடத்திற்கான விதைப்பு & பாசன பரிந்துரைகள்" },
          simulator: { title: "வாட்-இஃப் சிமுலேட்டர்", subtitle: "விவசாய பயன்பாட்டை சரிசெய்து நிலத்தடி நீர் எடுப்பில் ஏற்படும் மாற்றத்தை காண்க" },
          fieldData: { title: "சமூக கள தரவு", subtitle: "அதிகாரப்பூர்வ கேரள-மட்டும் மாவட்ட விவரங்களைத் தாண்டி தரவு பரப்பளவை விரிவுபடுத்த உண்மையான கிணறு ஆழ அளவீட்டைச் சமர்ப்பிக்கவும்." },
          reports: { title: "நிலத்தடி நீர் அறிக்கை", subtitle: "தரவு அட்டவணை மற்றும் AI-எழுதிய சுருக்கத்துடன் ஒரு PDF அறிக்கையை உருவாக்குங்கள்" },
          visualization: { title: "தரவு காட்சிப்படுத்தல்", subtitle: "நிலத்தடி நீர் தரவுத்தொகுப்புகளிலிருந்து விருப்ப விளக்கப்படங்களை உருவாக்குங்கள்" },
        },
        about: {
          tabs: { about: "எங்களைப் பற்றி", terms: "விதிமுறைகள் மற்றும் நிபந்தனைகள்", support: "உதவி மற்றும் ஆதரவு" },
          tagline: "இந்தியாவுக்கான நிலத்தடி நீர் நுண்ணறிவு, VioniX வடிவமைத்து உருவாக்கியது",
          footerLine1: "© 2026 ஜல்மித்ரா",
          footerLine2: "ஒரு VioniX தயாரிப்பு",
          whatIsTitle: "ஜல்மித்ரா என்றால் என்ன?",
          whatIsBody: "இந்தியாவின் மத்திய நிலத்தடி நீர் வாரியத்தின் (CGWB) மதிப்பீட்டு தரவை அடிப்படையாகக் கொண்ட ஒரு நிலத்தடி நீர் நுண்ணறிவு தளமே ஜல்மித்ரா. இது அடர்த்தியான ஆண்டு வள மதிப்பீடுகளை இயல்பான மொழி பதில்கள், முன்னறிவிப்புகள், விவசாயி ஆலோசனைகள் மற்றும் முன்கூட்டிய எச்சரிக்கைகளாக மாற்றுகிறது — தொழில்நுட்ப அறிக்கையைப் படிக்காமலேயே விவசாயிகள், கொள்கை நிர்ணயிப்பவர்கள், ஆராய்ச்சியாளர்கள் மற்றும் பொது மக்கள் நிலத்தடி நீர் நிலைமைகளைப் புரிந்துகொள்ள.",
          whatItDoesTitle: "இது என்ன செய்கிறது",
          features: [
            "ஆறு இந்திய மொழிகளில் அதிகாரப்பூர்வ நிலத்தடி நீர் தரவுத்தொகுப்புகளில் உரையாடல் கேள்வி-பதில்",
            "ஆண்டுகள் மற்றும் அளவீடுகள் வழியாக மாநில மற்றும் மாவட்ட ஒப்பீடுகள்",
            "பயன்பாடு மற்றும் கிடைக்கும் தன்மை போக்குகளின் முன்கணிப்பு பகுப்பாய்வு",
            "விவசாயிகளுக்கான பயிர்-குறிப்பிட்ட விதைப்பு மற்றும் பாசன ஆலோசனை",
            "சமூகம் சமர்ப்பித்த கள அளவீடுகள் மற்றும் PDF அறிக்கையிடல்",
          ],
          devTitle: "VioniX ஆல் உருவாக்கப்பட்டது",
          devBody: "பொது நலனுக்காக நடைமுறை, தரவு-சார்ந்த கருவிகளை உருவாக்குவதில் கவனம் செலுத்தும் ஒரு சிறிய தயாரிப்பு ஸ்டுடியோ ஆன VioniX ஆல் ஜல்மித்ரா வடிவமைக்கப்பட்டு உருவாக்கப்பட்டது. வடிவமைப்பு, பொறியியல் மற்றும் ஜல்மித்ராவின் உரையாடல் மற்றும் பகுப்பாய்வு திறன்களுக்கு ஆற்றல் அளிக்கும் AI அமைப்புகளை VioniX கையாள்கிறது.",
          lastUpdated: "கடைசியாக புதுப்பிக்கப்பட்டது 2026",
          clauses: [
            { title: "1. விதிமுறைகளை ஏற்றுக்கொள்ளுதல்", body: "ஜல்மித்ராவை அணுகுவதன் மூலமோ பயன்படுத்துவதன் மூலமோ, இந்த விதிமுறைகள் மற்றும் நிபந்தனைகளுக்கு நீங்கள் ஒப்புக்கொள்கிறீர்கள். நீங்கள் ஒப்புக்கொள்ளவில்லை எனில், தயவுசெய்து இந்த தளத்தின் பயன்பாட்டை நிறுத்தவும்." },
            { title: "2. சேவையின் தன்மை", body: "CGWB நிலத்தடி நீர் மதிப்பீட்டு தரவு மற்றும் தொடர்புடைய பொது தரவுத்தொகுப்புகளிலிருந்து பெறப்பட்ட தகவல் சுருக்கங்கள், முன்னறிவிப்புகள் மற்றும் பரிந்துரைகளை ஜல்மித்ரா வழங்குகிறது. இது ஒரு முடிவு-ஆதரவு கருவி, அதிகாரப்பூர்வ ஆய்வுகள், உரிமம் பெற்ற நிலத்தடி நீரியல் ஆலோசனை அல்லது ஒழுங்குமுறை ஒப்புதலுக்கு மாற்றாக இல்லை." },
            { title: "3. தரவு துல்லியம்", body: "அடிப்படை தரவுத்தொகுப்புகளை புதுப்பித்த நிலையில் வைத்திருக்க நாங்கள் நியாயமான கவனம் எடுத்தாலும், நிலத்தடி நீர் மதிப்பீடுகள் அவ்வப்போது வெளியிடப்படுகின்றன, நிகழ்நேர நிலைமைகளை பிரதிபலிக்காமல் இருக்கலாம். எண்கள், முன்னறிவிப்புகள் மற்றும் AI-உருவாக்கிய சுருக்கங்களில் பிழைகள் இருக்கலாம் — ஒரு உண்மையான முடிவுக்கு முக்கியமான எண்களை எப்போதும் அதிகாரப்பூர்வ CGWB மூலங்களுடன் சரிபார்க்கவும்." },
            { title: "4. உத்தரவாதம் இல்லை", body: "ஜல்மித்ரா எந்தவிதமான உத்தரவாதமும் இல்லாமல் \"இருப்பது போலவே\" வழங்கப்படுகிறது. சேவையின் தடையற்ற கிடைக்கும் தன்மையை VioniX உத்தரவாதம் அளிக்காது." },
            { title: "5. பொறுப்பு வரம்பு", body: "ஜல்மித்ரா வழங்கும் தகவலின் அடிப்படையில் எடுக்கப்படும் விவசாய, நிதி அல்லது கொள்கை முடிவுகளால் ஏற்படும் எந்த இழப்பு அல்லது சேதத்திற்கும் VioniX பொறுப்பேற்காது." },
            { title: "6. சமூக தரவு", body: "பயனர்களால் சமர்ப்பிக்கப்படும் கள அவதானிப்புகள் சமூகம்-அறிவித்தவை மற்றும் சரிபார்க்கப்படாதவை என தெளிவாக குறிக்கப்படுகின்றன. இவை ஒருபோதும் அதிகாரப்பூர்வ CGWB பதிவுகளுடன் அமைதியாக இணைக்கப்படாது." },
            { title: "7. ஏற்றுக்கொள்ளத்தக்க பயன்பாடு", body: "தளத்தின் உள்கட்டமைப்பை சீர்குலைக்க முயற்சிப்பது, அனுமதியின்றி பெருமளவில் தரவை சேகரிப்பது, அல்லது தவறான கள அவதானிப்புகளை சமர்ப்பிப்பது உள்ளிட்ட தளத்தை தவறாக பயன்படுத்த மாட்டீர்கள் என்பதை நீங்கள் ஒப்புக்கொள்கிறீர்கள்." },
            { title: "8. இந்த விதிமுறைகளில் மாற்றங்கள்", body: "தளம் வளர்ச்சியடையும் போது இந்த விதிமுறைகள் அவ்வப்போது புதுப்பிக்கப்படலாம். மாற்றங்களுக்குப் பிறகு ஜல்மித்ராவைத் தொடர்ந்து பயன்படுத்துவது திருத்தப்பட்ட விதிமுறைகளை ஏற்றுக்கொள்வதாகும்." },
          ],
          contactTitle: "VioniX ஆதரவைத் தொடர்பு கொள்ளவும்",
          contactBody: "பிழை அறிக்கைகள், தரவு திருத்தங்கள், கூட்டாண்மை கேள்விகள் அல்லது வேறு எதற்கும் — எங்களைத் தொடர்பு கொள்ளுங்கள், நாங்கள் உங்களைத் தொடர்பு கொள்வோம்.",
          faqTitle: "அடிக்கடி கேட்கப்படும் கேள்விகள்",
          faq: [
            { q: "அடிப்படை தரவு எங்கிருந்து வருகிறது?", a: "CGWB மற்றும் மாநில/யூனியன் பிரதேச நிலத்தடி நீர் துறைகளால் ஆண்டுதோறும் வெளியிடப்படும் இந்தியாவின் டைனமிக் நிலத்தடி நீர் வளங்களின் மதிப்பீட்டிலிருந்து நிலத்தடி நீர் எண்கள் வருகின்றன." },
            { q: "AI-உருவாக்கிய பதில்களை என்னால் நம்ப முடியுமா?", a: "ஜல்மித்ரா தனது மூலங்களை மேற்கோள் காட்டுகிறது மற்றும் அடிப்படை தரவுத்தொகுப்பில் அடிப்படையாகக் கொண்டிருக்க உருவாக்கப்பட்டுள்ளது, ஆனால் எந்த AI அமைப்பையும் போலவே இது தவறுகள் செய்யக்கூடும் — ஒரு உண்மையான முடிவுக்கு முக்கியமான எண்களை எப்போதும் மீண்டும் சரிபார்க்கவும்." },
            { q: "ஆப் மொழியை நான் எப்படி மாற்றுவது?", a: "எந்தப் பக்கத்திலும் மேல் வழிசெலுத்தல் பட்டியில் உள்ள மொழி மாற்றியைப் பயன்படுத்தவும் — உங்கள் தேர்வு முழு ஆப்பிலும் நினைவில் வைக்கப்படும்." },
            { q: "நான் சமர்ப்பித்த கள தரவு எவ்வாறு பயன்படுத்தப்படுகிறது?", a: "சமூக கள அவதானிப்புகள் அதிகாரப்பூர்வ பதிவுகளிலிருந்து தனித்தனியாக சேமிக்கப்படுகின்றன, எப்போதும் சரிபார்க்கப்படாத, சமூகம்-அறிவித்த தரவாக குறிக்கப்படுகின்றன." },
          ],
        },
        greeting: "வணக்கம்! நான் ஜல்மித்ரா, உங்கள் நிலத்தடி நீர் உதவியாளர். நான் உங்களுக்கு நிலத்தடி நீர் வளங்கள் தரவு, வரலாற்று மதிப்பீடுகள் மற்றும் இந்தியாவின் நிலத்தடி நீர் வளங்கள் பற்றிய தகவலை வழங்க முடியும். நான் உங்களுக்கு இன்று எப்படி உதவலாம்?",
        chatCleared: "அரட்டை அழிக்கப்பட்டது. இன்று நான் உங்களுக்கு நிலத்தடி நீர் தரவுகளில் எப்படி உதவலாம்?",
        quickQueries: {
          q1: "கர்நாடகாவில் நிலத்தடி நீர்மட்டம் காண்பி",
          q2: "மகாராஷ்டிராவின் முக்கிய பகுதிகள்",
          q3: "தமிழ்நாட்டின் வரலாற்றுத் தரவு",
          q4: "ரீசார்ஜ் மதிப்பீட்டு முறை",
          q5: "பஞ்சாபின் அதிகப்படியான பயன்பாட்டு பகுதிகள்",
          q6: "நிலத்தடி நீரின் தர மதிப்பீடு",
          q7: "கேரளாவில் நிலத்தடி நீரின் தரம்",
          q8: "ராஜஸ்தானின் தற்போதைய மதிப்பீடு"
        },
        roles: {
          farmer: {
            title: "விவசாயி",
            description: "விவசாய நீர் மேலாண்மைக்கான நடைமுறை ஆலோசனைகள்",
            focus: "பயிர் திட்டமிடல், பாசன வழிகாட்டுதல் மற்றும் கிணறு மேலாண்மைக்கு பயன்படும் தகவலை பெறுங்கள்"
          },
          policymaker: {
            title: "கொள்கை நிர்ணயிப்பவர்",
            description: "கொள்கை பார்வைகள் மற்றும் ஆட்சி பரிந்துரைகள்",
            focus: "கொள்கை சார்ந்த தரவு, விதிமுறை தகவல் மற்றும் ஆட்சி பரிந்துரைகள் பெறுங்கள்"
          },
          researcher: {
            title: "ஆராய்ச்சியாளர்",
            description: "விரிவான தரவுகள் மற்றும் தொழில்நுட்ப பகுப்பாய்வு",
            focus: "விரிவான தரவுத்தொகுப்புகள், முறைகள் மற்றும் தொழில்நுட்ப பகுப்பாய்வு பெறுங்கள்"
          },
          general: {
            title: "பொது பயனர்",
            description: "பொது தகவல் மற்றும் அடிப்படை பார்வைகள்",
            focus: "நிலத்தடி நீர் வளங்கள் பற்றிய எளிய தகவலைப் பெறுங்கள்"
          }
        }
      }
    },
    kn: {
      translation: {
        heroTitle: "ಇಂದು ಅಂತರ್ಜಲದ ಬಗ್ಗೆ ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?",
        tryAsking: "ಹೀಗೆ ಕೇಳಿ ನೋಡಿ",
        suggestions: "ಸಲಹೆಗಳು",
        inputPlaceholder: "ಯಾವುದೇ ಪ್ರದೇಶದ ಅಂತರ್ಜಲದ ಬಗ್ಗೆ ಕೇಳಿ...",
        inputDisclaimer: "ಜಲಮಿತ್ರ ತಪ್ಪುಗಳನ್ನು ಮಾಡಬಹುದು. ಪ್ರಮುಖ ಅಂತರ್ಜಲ ಅಂಕಿಅಂಶಗಳನ್ನು ಅಧಿಕೃತ CGWB ಮೂಲಗಳೊಂದಿಗೆ ಪರಿಶೀಲಿಸಿ.",
        thinking: "ಯೋಚಿಸುತ್ತಿದೆ...",
        backToChat: "ಚಾಟ್‌ಗೆ ಹಿಂತಿರುಗಿ",
        nav: {
          subtitle: "ಅಂತರ್ಜಲ ದತ್ತಾಂಶ ಮತ್ತು ಸಲಹಾ ವೇದಿಕೆ",
          map: "ನಕ್ಷೆ",
          visualize: "ದೃಶ್ಯೀಕರಿಸಿ",
          voice: "ಧ್ವನಿ",
        },
        sidebar: {
          newChat: "ಹೊಸ ಚಾಟ್",
          yourRole: "ನಿಮ್ಮ ಪಾತ್ರ",
          explore: "ಅನ್ವೇಷಿಸಿ",
          clear: "ತೆರವುಗೊಳಿಸಿ",
          chattingAs: "ಈ ಪಾತ್ರದಲ್ಲಿ ಚಾಟ್ ಮಾಡುತ್ತಿದ್ದೀರಿ",
        },
        actions: {
          compare: "ರಾಜ್ಯಗಳನ್ನು ಹೋಲಿಸಿ",
          forecast: "ಮುನ್ಸೂಚನೆ",
          advisory: "ರೈತ ಸಲಹೆ",
          simulator: "ವಾಟ್-ಇಫ್ ಸಿಮ್ಯುಲೇಟರ್",
          fieldData: "ಫೀಲ್ಡ್ ಡೇಟಾ ಸಲ್ಲಿಸಿ",
          reports: "ವರದಿಗಳು",
        },
        pages: {
          map: { title: "ಭಾರತ ಅಂತರ್ಜಲ ನಕ್ಷೆ", subtitle: "ಪ್ರತಿ ರಾಜ್ಯ ಮತ್ತು ಕೇಂದ್ರಾಡಳಿತ ಪ್ರದೇಶದ ನೇರ ಅಂತರ್ಜಲ ಮಟ್ಟಗಳು" },
          compare: { title: "ತುಲನಾತ್ಮಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್", subtitle: "ರಾಜ್ಯಗಳು, ವರ್ಷಗಳು ಮತ್ತು ಮಾಪನಗಳಾದ್ಯಂತ ಅಂತರ್ಜಲ ದತ್ತಾಂಶವನ್ನು ಹೋಲಿಸಿ" },
          forecast: { title: "ಅಂತರ್ಜಲ ಮುನ್ಸೂಚನೆ", subtitle: "ಅಂತರ್ಜಲ ಬಳಕೆ ಮತ್ತು ಲಭ್ಯತೆಯ ಪ್ರವೃತ್ತಿಗಳ ಮುನ್ಸೂಚನಾ ವಿಶ್ಲೇಷಣೆ" },
          advisory: { title: "ರೈತ ಸಲಹೆ", subtitle: "ನಿಮ್ಮ ಬೆಳೆ ಮತ್ತು ಸ್ಥಳಕ್ಕೆ ಬಿತ್ತನೆ & ನೀರಾವರಿ ಶಿಫಾರಸುಗಳು" },
          simulator: { title: "ವಾಟ್-ಇಫ್ ಸಿಮ್ಯುಲೇಟರ್", subtitle: "ಕೃಷಿ ಬಳಕೆಯನ್ನು ಸರಿಹೊಂದಿಸಿ ಮತ್ತು ಅಂತರ್ಜಲ ಬಳಕೆಯ ಮೇಲಿನ ಅಂದಾಜು ಪರಿಣಾಮವನ್ನು ನೋಡಿ" },
          fieldData: { title: "ಸಮುದಾಯ ಫೀಲ್ಡ್ ಡೇಟಾ", subtitle: "ಅಧಿಕೃತ ಕೇರಳ-ಮಾತ್ರ ಜಿಲ್ಲಾ ವಿವರವನ್ನು ಮೀರಿ ದತ್ತಾಂಶ ವ್ಯಾಪ್ತಿಯನ್ನು ವಿಸ್ತರಿಸಲು ನೈಜ ಬಾವಿ ಆಳದ ಓದುವಿಕೆಯನ್ನು ಸಲ್ಲಿಸಿ." },
          reports: { title: "ಅಂತರ್ಜಲ ವರದಿ", subtitle: "ದತ್ತಾಂಶ ಕೋಷ್ಟಕ ಮತ್ತು AI-ಬರೆದ ಸಾರಾಂಶದೊಂದಿಗೆ PDF ವರದಿಯನ್ನು ರಚಿಸಿ" },
          visualization: { title: "ದತ್ತಾಂಶ ದೃಶ್ಯೀಕರಣ", subtitle: "ಅಂತರ್ಜಲ ದತ್ತಾಂಶಗಳಿಂದ ಕಸ್ಟಮ್ ಚಾರ್ಟ್‌ಗಳನ್ನು ರಚಿಸಿ" },
        },
        about: {
          tabs: { about: "ಬಗ್ಗೆ", terms: "ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು", support: "ಸಹಾಯ ಮತ್ತು ಬೆಂಬಲ" },
          tagline: "ಭಾರತಕ್ಕಾಗಿ ಅಂತರ್ಜಲ ಬುದ್ಧಿಮತ್ತೆ, VioniX ವಿನ್ಯಾಸಗೊಳಿಸಿ ನಿರ್ಮಿಸಿದೆ",
          footerLine1: "© 2026 ಜಲಮಿತ್ರ",
          footerLine2: "ಒಂದು VioniX ಉತ್ಪನ್ನ",
          whatIsTitle: "ಜಲಮಿತ್ರ ಎಂದರೇನು?",
          whatIsBody: "ಭಾರತದ ಕೇಂದ್ರ ಅಂತರ್ಜಲ ಮಂಡಳಿಯ (CGWB) ಮೌಲ್ಯಮಾಪನ ದತ್ತಾಂಶದ ಮೇಲೆ ನಿರ್ಮಿಸಲಾದ ಅಂತರ್ಜಲ ಬುದ್ಧಿಮತ್ತೆ ವೇದಿಕೆ ಜಲಮಿತ್ರ. ಇದು ದಟ್ಟವಾದ ವಾರ್ಷಿಕ ಸಂಪನ್ಮೂಲ ಮೌಲ್ಯಮಾಪನಗಳನ್ನು ಸ್ವಾಭಾವಿಕ ಭಾಷೆಯ ಉತ್ತರಗಳು, ಮುನ್ಸೂಚನೆಗಳು, ರೈತ ಸಲಹೆಗಳು ಮತ್ತು ಪೂರ್ವ-ಎಚ್ಚರಿಕೆ ಅಲರ್ಟ್‌ಗಳಾಗಿ ಪರಿವರ್ತಿಸುತ್ತದೆ — ತಾಂತ್ರಿಕ ವರದಿಯನ್ನು ಓದದೆಯೇ ರೈತರು, ನೀತಿನಿರ್ಣಯಕರು, ಸಂಶೋಧಕರು ಮತ್ತು ಸಾರ್ವಜನಿಕರು ಅಂತರ್ಜಲ ಪರಿಸ್ಥಿತಿಗಳನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು.",
          whatItDoesTitle: "ಇದು ಏನು ಮಾಡುತ್ತದೆ",
          features: [
            "ಆರು ಭಾರತೀಯ ಭಾಷೆಗಳಲ್ಲಿ ಅಧಿಕೃತ ಅಂತರ್ಜಲ ದತ್ತಾಂಶಗಳ ಮೇಲೆ ಸಂಭಾಷಣಾತ್ಮಕ ಪ್ರಶ್ನೋತ್ತರ",
            "ವರ್ಷಗಳು ಮತ್ತು ಮಾಪನಗಳಾದ್ಯಂತ ರಾಜ್ಯ ಮತ್ತು ಜಿಲ್ಲಾ ಹೋಲಿಕೆಗಳು",
            "ಬಳಕೆ ಮತ್ತು ಲಭ್ಯತೆಯ ಪ್ರವೃತ್ತಿಗಳ ಮುನ್ಸೂಚನಾ ವಿಶ್ಲೇಷಣೆ",
            "ರೈತರಿಗಾಗಿ ಬೆಳೆ-ನಿರ್ದಿಷ್ಟ ಬಿತ್ತನೆ ಮತ್ತು ನೀರಾವರಿ ಸಲಹೆ",
            "ಸಮುದಾಯ ಸಲ್ಲಿಸಿದ ಫೀಲ್ಡ್ ರೀಡಿಂಗ್‌ಗಳು ಮತ್ತು PDF ವರದಿ",
          ],
          devTitle: "VioniX ನಿಂದ ಅಭಿವೃದ್ಧಿಪಡಿಸಲಾಗಿದೆ",
          devBody: "ಸಾರ್ವಜನಿಕ ಒಳಿತಿಗಾಗಿ ಪ್ರಾಯೋಗಿಕ, ದತ್ತಾಂಶ-ಆಧಾರಿತ ಸಾಧನಗಳನ್ನು ನಿರ್ಮಿಸುವಲ್ಲಿ ಗಮನ ಹರಿಸುವ ಸಣ್ಣ ಉತ್ಪನ್ನ ಸ್ಟುಡಿಯೋ ಆಗಿರುವ VioniX ನಿಂದ ಜಲಮಿತ್ರ ವಿನ್ಯಾಸಗೊಳಿಸಿ ಅಭಿವೃದ್ಧಿಪಡಿಸಲಾಗಿದೆ. ವಿನ್ಯಾಸ, ಎಂಜಿನಿಯರಿಂಗ್ ಮತ್ತು ಜಲಮಿತ್ರದ ಸಂಭಾಷಣಾತ್ಮಕ ಮತ್ತು ವಿಶ್ಲೇಷಣಾತ್ಮಕ ವೈಶಿಷ್ಟ್ಯಗಳಿಗೆ ಶಕ್ತಿ ನೀಡುವ AI ವ್ಯವಸ್ಥೆಗಳನ್ನು VioniX ನಿರ್ವಹಿಸುತ್ತದೆ.",
          lastUpdated: "ಕೊನೆಯದಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ 2026",
          clauses: [
            { title: "1. ನಿಯಮಗಳ ಸ್ವೀಕಾರ", body: "ಜಲಮಿತ್ರವನ್ನು ಬಳಸುವ ಮೂಲಕ, ನೀವು ಈ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳಿಗೆ ಒಪ್ಪುತ್ತೀರಿ. ನೀವು ಒಪ್ಪದಿದ್ದರೆ, ದಯವಿಟ್ಟು ಈ ವೇದಿಕೆಯ ಬಳಕೆಯನ್ನು ನಿಲ್ಲಿಸಿ." },
            { title: "2. ಸೇವೆಯ ಸ್ವರೂಪ", body: "ಜಲಮಿತ್ರ CGWB ಅಂತರ್ಜಲ ಮೌಲ್ಯಮಾಪನ ದತ್ತಾಂಶ ಮತ್ತು ಸಂಬಂಧಿತ ಸಾರ್ವಜನಿಕ ದತ್ತಾಂಶಗಳಿಂದ ಪಡೆದ ಮಾಹಿತಿ ಸಾರಾಂಶಗಳು, ಮುನ್ಸೂಚನೆಗಳು ಮತ್ತು ಶಿಫಾರಸುಗಳನ್ನು ಒದಗಿಸುತ್ತದೆ. ಇದು ನಿರ್ಧಾರ-ಬೆಂಬಲ ಸಾಧನ, ಅಧಿಕೃತ ಸಮೀಕ್ಷೆಗಳಿಗೆ, ಪರವಾನಗಿ ಪಡೆದ ಜಲಭೂವಿಜ್ಞಾನ ಸಲಹೆಗೆ, ಅಥವಾ ನಿಯಂತ್ರಕ ಅನುಮೋದನೆಗೆ ಪರ್ಯಾಯವಲ್ಲ." },
            { title: "3. ದತ್ತಾಂಶ ನಿಖರತೆ", body: "ಆಧಾರವಾಗಿರುವ ದತ್ತಾಂಶಗಳನ್ನು ಪ್ರಸ್ತುತವಾಗಿಡಲು ನಾವು ಸಮಂಜಸ ಕಾಳಜಿ ವಹಿಸಿದರೂ, ಅಂತರ್ಜಲ ಮೌಲ್ಯಮಾಪನಗಳು ನಿಯತಕಾಲಿಕವಾಗಿ ಪ್ರಕಟವಾಗುತ್ತವೆ ಮತ್ತು ನೈಜ-ಸಮಯದ ಪರಿಸ್ಥಿತಿಗಳನ್ನು ಪ್ರತಿಬಿಂಬಿಸದೇ ಇರಬಹುದು. ಅಂಕಿಅಂಶಗಳು, ಮುನ್ಸೂಚನೆಗಳು ಮತ್ತು AI-ರಚಿತ ಸಾರಾಂಶಗಳಲ್ಲಿ ದೋಷಗಳು ಇರಬಹುದು — ನಿಜವಾದ ನಿರ್ಧಾರಕ್ಕೆ ಮುಖ್ಯವಾದ ಅಂಕಿಅಂಶಗಳನ್ನು ಯಾವಾಗಲೂ ಅಧಿಕೃತ CGWB ಮೂಲಗಳ ವಿರುದ್ಧ ಪರಿಶೀಲಿಸಿ." },
            { title: "4. ಯಾವುದೇ ವಾರಂಟಿ ಇಲ್ಲ", body: "ಜಲಮಿತ್ರವನ್ನು ಯಾವುದೇ ರೀತಿಯ ವಾರಂಟಿ ಇಲ್ಲದೆ \"ಇರುವ ಹಾಗೆ\" ಒದಗಿಸಲಾಗುತ್ತದೆ. ಸೇವೆಯ ನಿರಂತರ ಲಭ್ಯತೆಗೆ VioniX ಖಾತರಿ ನೀಡುವುದಿಲ್ಲ." },
            { title: "5. ಹೊಣೆಗಾರಿಕೆಯ ಮಿತಿ", body: "ಜಲಮಿತ್ರ ಒದಗಿಸಿದ ಮಾಹಿತಿಯ ಆಧಾರದ ಮೇಲೆ ತೆಗೆದುಕೊಂಡ ಕೃಷಿ, ಆರ್ಥಿಕ ಅಥವಾ ನೀತಿ ನಿರ್ಧಾರಗಳಿಂದ ಉಂಟಾಗುವ ಯಾವುದೇ ನಷ್ಟ ಅಥವಾ ಹಾನಿಗೆ VioniX ಹೊಣೆಗಾರರಾಗಿರುವುದಿಲ್ಲ." },
            { title: "6. ಸಮುದಾಯ ದತ್ತಾಂಶ", body: "ಬಳಕೆದಾರರು ಸಲ್ಲಿಸಿದ ಫೀಲ್ಡ್ ಅವಲೋಕನಗಳನ್ನು ಸಮುದಾಯ-ವರದಿ ಮಾಡಿದ ಮತ್ತು ಪರಿಶೀಲಿಸದ ಎಂದು ಸ್ಪಷ್ಟವಾಗಿ ಲೇಬಲ್ ಮಾಡಲಾಗುತ್ತದೆ. ಇವುಗಳನ್ನು ಎಂದಿಗೂ ಅಧಿಕೃತ CGWB ದಾಖಲೆಗಳೊಂದಿಗೆ ಮೌನವಾಗಿ ವಿಲೀನಗೊಳಿಸಲಾಗುವುದಿಲ್ಲ." },
            { title: "7. ಸ್ವೀಕಾರಾರ್ಹ ಬಳಕೆ", body: "ವೇದಿಕೆಯ ಮೂಲಸೌಕರ್ಯವನ್ನು ಅಡ್ಡಿಪಡಿಸಲು ಪ್ರಯತ್ನಿಸುವುದು, ಅನುಮತಿ ಇಲ್ಲದೆ ದೊಡ್ಡ ಪ್ರಮಾಣದಲ್ಲಿ ದತ್ತಾಂಶವನ್ನು ಸಂಗ್ರಹಿಸುವುದು, ಅಥವಾ ಸುಳ್ಳು ಫೀಲ್ಡ್ ಅವಲೋಕನಗಳನ್ನು ಸಲ್ಲಿಸುವುದು ಸೇರಿದಂತೆ ವೇದಿಕೆಯನ್ನು ದುರುಪಯೋಗಪಡಿಸಿಕೊಳ್ಳುವುದಿಲ್ಲ ಎಂದು ನೀವು ಒಪ್ಪುತ್ತೀರಿ." },
            { title: "8. ಈ ನಿಯಮಗಳಲ್ಲಿನ ಬದಲಾವಣೆಗಳು", body: "ವೇದಿಕೆಯು ವಿಕಸನಗೊಳ್ಳುತ್ತಿದ್ದಂತೆ ಈ ನಿಯಮಗಳನ್ನು ಕಾಲಕಾಲಕ್ಕೆ ನವೀಕರಿಸಬಹುದು. ಬದಲಾವಣೆಗಳ ನಂತರ ಜಲಮಿತ್ರವನ್ನು ಮುಂದುವರಿಸಿ ಬಳಸುವುದು ಪರಿಷ್ಕೃತ ನಿಯಮಗಳ ಸ್ವೀಕಾರವೆಂದು ಪರಿಗಣಿಸಲಾಗುತ್ತದೆ." },
          ],
          contactTitle: "VioniX ಬೆಂಬಲವನ್ನು ಸಂಪರ್ಕಿಸಿ",
          contactBody: "ದೋಷ ವರದಿಗಳು, ದತ್ತಾಂಶ ತಿದ್ದುಪಡಿಗಳು, ಪಾಲುದಾರಿಕೆ ಪ್ರಶ್ನೆಗಳು, ಅಥವಾ ಬೇರೆ ಯಾವುದಕ್ಕಾದರೂ — ಸಂಪರ್ಕಿಸಿ, ನಾವು ನಿಮ್ಮನ್ನು ಮತ್ತೆ ಸಂಪರ್ಕಿಸುತ್ತೇವೆ.",
          faqTitle: "ಪದೇ ಪದೇ ಕೇಳಲಾಗುವ ಪ್ರಶ್ನೆಗಳು",
          faq: [
            { q: "ಆಧಾರವಾಗಿರುವ ದತ್ತಾಂಶ ಎಲ್ಲಿಂದ ಬರುತ್ತದೆ?", a: "CGWB ಮತ್ತು ರಾಜ್ಯ/ಕೇಂದ್ರಾಡಳಿತ ಪ್ರದೇಶ ಅಂತರ್ಜಲ ಇಲಾಖೆಗಳು ವಾರ್ಷಿಕವಾಗಿ ಪ್ರಕಟಿಸುವ ಭಾರತದ ಡೈನಾಮಿಕ್ ಅಂತರ್ಜಲ ಸಂಪನ್ಮೂಲಗಳ ಮೌಲ್ಯಮಾಪನದಿಂದ ಅಂತರ್ಜಲ ಅಂಕಿಅಂಶಗಳು ಬರುತ್ತವೆ." },
            { q: "ನಾನು AI-ರಚಿತ ಉತ್ತರಗಳನ್ನು ನಂಬಬಹುದೇ?", a: "ಜಲಮಿತ್ರ ತನ್ನ ಮೂಲಗಳನ್ನು ಉಲ್ಲೇಖಿಸುತ್ತದೆ ಮತ್ತು ಆಧಾರವಾಗಿರುವ ದತ್ತಾಂಶದಲ್ಲಿ ಆಧಾರಿತವಾಗಿ ಉಳಿಯುವಂತೆ ನಿರ್ಮಿಸಲಾಗಿದೆ, ಆದರೆ ಯಾವುದೇ AI ವ್ಯವಸ್ಥೆಯಂತೆ ಇದು ತಪ್ಪುಗಳನ್ನು ಮಾಡಬಹುದು — ನಿಜವಾದ ನಿರ್ಧಾರಕ್ಕೆ ಮುಖ್ಯವಾದ ಅಂಕಿಅಂಶಗಳನ್ನು ಯಾವಾಗಲೂ ಮತ್ತೆ ಪರಿಶೀಲಿಸಿ." },
            { q: "ನಾನು ಅಪ್ಲಿಕೇಶನ್ ಭಾಷೆಯನ್ನು ಹೇಗೆ ಬದಲಾಯಿಸುವುದು?", a: "ಯಾವುದೇ ಪುಟದಲ್ಲಿನ ಮೇಲಿನ ನ್ಯಾವಿಗೇಷನ್ ಬಾರ್‌ನಲ್ಲಿರುವ ಭಾಷಾ ಸ್ವಿಚರ್ ಅನ್ನು ಬಳಸಿ — ನಿಮ್ಮ ಆಯ್ಕೆಯನ್ನು ಸಂಪೂರ್ಣ ಅಪ್ಲಿಕೇಶನ್‌ನಾದ್ಯಂತ ನೆನಪಿಡಲಾಗುತ್ತದೆ." },
            { q: "ನಾನು ಸಲ್ಲಿಸಿದ ಫೀಲ್ಡ್ ದತ್ತಾಂಶವನ್ನು ಹೇಗೆ ಬಳಸಲಾಗುತ್ತದೆ?", a: "ಸಮುದಾಯ ಫೀಲ್ಡ್ ಅವಲೋಕನಗಳನ್ನು ಅಧಿಕೃತ ದಾಖಲೆಗಳಿಂದ ಪ್ರತ್ಯೇಕವಾಗಿ ಸಂಗ್ರಹಿಸಲಾಗುತ್ತದೆ ಮತ್ತು ಯಾವಾಗಲೂ ಪರಿಶೀಲಿಸದ, ಸಮುದಾಯ-ವರದಿ ಮಾಡಿದ ದತ್ತಾಂಶವೆಂದು ಲೇಬಲ್ ಮಾಡಲಾಗುತ್ತದೆ." },
          ],
        },
        greeting: "ನಮಸ್ಕಾರ! ನಾನು ಜಲಮಿತ್ರ, ನಿಮ್ಮ ಅಂತರ್ಜಲ ಸಹಾಯಕ. ನಾನು ನಿಮಗೆ ಅಂತರ್ಜಲ ಸಂಪನ್ಮೂಲಗಳ ಡೇಟಾ, ಐತಿಹಾಸಿಕ ಮೌಲ್ಯಮಾಪನ ಮತ್ತು ಭಾರತದ ಡೈನಾಮಿಕ್ ಭೂಗರ್ಭಜಲ ಸಂಪನ್ಮೂಲಗಳ ಕುರಿತು ಮಾಹಿತಿ ನೀಡಬಹುದು. ನಾನು ನಿಮಗೆ ಇಂದು ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?",
        chatCleared: "ಚಾಟ್ ಕ್ಲಿಯರ್ ಮಾಡಲಾಗಿದೆ. ಇಂದು ನಾನು ನಿಮಗೆ ಭೂಗರ್ಭಜಲ ಡೇಟಾದಲ್ಲಿ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?",
        quickQueries: {
          q1: "ಕರ್ನಾಟಕದಲ್ಲಿ ಭೂಗರ್ಭಜಲ ಮಟ್ಟ ತೋರಿಸಿ",
          q2: "ಮಹಾರಾಷ್ಟ್ರದ ಪ್ರಮುಖ ಬ್ಲಾಕ್‌ಗಳು",
          q3: "ತಮಿಳುನಾಡಿನ ಐತಿಹಾಸಿಕ ಡೇಟಾ",
          q4: "ರೀಚಾರ್ಜ್ ಮೌಲ್ಯಮಾಪನ ವಿಧಾನ",
          q5: "ಪಂಜಾಬ್‌ನಲ್ಲಿ ಅತಿಯಾಗಿ ಬಳಸಿದ ಪ್ರದೇಶಗಳು",
          q6: "ಭೂಗರ್ಭಜಲ ಗುಣಮಟ್ಟದ ಮೌಲ್ಯಮಾಪನ",
          q7: "ಕೇರಳದಲ್ಲಿ ಭೂಗರ್ಭಜಲ ಗುಣಮಟ್ಟ",
          q8: "ರಾಜಸ್ಥಾನದ ಪ್ರಸ್ತುತ ಮೌಲ್ಯಮಾಪನ"
        },
        roles: {
          farmer: {
            title: "ಕೃಷಿಕ",
            description: "ಕೃಷಿ ನೀರಿನ ನಿರ್ವಹಣೆಗೆ ಪ್ರಾಯೋಗಿಕ ಸಲಹೆಗಳು",
            focus: "ಬೆಳೆ ಯೋಜನೆ, ನೀರಾವರಿ ಮಾರ್ಗದರ್ಶನ ಮತ್ತು ಬಾವಿ ನಿರ್ವಹಣೆಗೆ ಉಪಯುಕ್ತ ಮಾಹಿತಿಯನ್ನು ಪಡೆಯಿರಿ"
          },
          policymaker: {
            title: "ನೀತಿನಿರ್ಣಯಕ",
            description: "ನೀತಿ ಒಳನೋಟಗಳು ಮತ್ತು ಆಡಳಿತ ಸಲಹೆಗಳು",
            focus: "ನೀತಿ ಸಂಬಂಧಿತ ಡೇಟಾ, ನಿಯಂತ್ರಣ ಮಾಹಿತಿ ಮತ್ತು ಆಡಳಿತ ಶಿಫಾರಸುಗಳನ್ನು ಪಡೆಯಿರಿ"
          },
          researcher: {
            title: "ಶೋಧಕ",
            description: "ವಿಸ್ತೃತ ಡೇಟಾ ಮತ್ತು ತಾಂತ್ರಿಕ ವಿಶ್ಲೇಷಣೆ",
            focus: "ವ್ಯಾಪಕ ಡೇಟಾಸೆಟ್‌ಗಳು, ವಿಧಾನಗಳು ಮತ್ತು ತಾಂತ್ರಿಕ ವಿಶ್ಲೇಷಣೆಯನ್ನು ಪಡೆಯಿರಿ"
          },
          general: {
            title: "ಸಾಮಾನ್ಯ ಬಳಕೆದಾರ",
            description: "ಸಾಮಾನ್ಯ ಮಾಹಿತಿ ಮತ್ತು ಮೂಲಭೂತ ಒಳನೋಟಗಳು",
            focus: "ಭೂಗರ್ಭಜಲ ಸಂಪನ್ಮೂಲಗಳ ಬಗ್ಗೆ ಸರಳ ಮಾಹಿತಿಯನ್ನು ಪಡೆಯಿರಿ"
          }
        }
      }
    }
  },
  lng: (typeof window !== "undefined" && localStorage.getItem("jalmitra_language")) || "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false }
});

export default i18n;
